import { Button } from "@/components/design-system/button";
import FilterWithRadio from "@/components/design-system/FilterWithRadio";
import ListPagination from "@/components/design-system/list-pagination";
import Modal from "@/components/design-system/Modal";
import {
  Table,
  type TableColumn,
  type TableSortConfig,
} from "@/components/design-system/table";
import {
  showApiErrorToast,
  showApiSuccessToast,
} from "../utils/apiToastMessage";
import { bindLabelTextInputHandlers } from "@/utils/formTextField";
import { getLabelTextValidationError } from "@/utils/zodString";
import { SquarePen, Trash2 } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { ADMIN_LEVEL_USE_MOCK } from "../config/admin.config";
import { levelApi } from "../api/levelApi";
import { LEVELS_PER_PAGE } from "../constants/class.constants";
import type { LevelTableRow, LevelTier } from "../types/level.types";
import LevelValueEditModal from "./LevelValueEditModal";

const FORM_LABEL_CLASS =
  "text-[16px] font-medium leading-normal text-[#2D3748]";
const LEVEL_VALUE_INPUT_CLASS =
  "h-[50px] w-full rounded-[15px] border border-[#E2E8F0] bg-white px-[20px] py-[13px] text-[16px] font-medium text-[#2E2E32] placeholder:text-[#A0AEC0] focus:outline-none sm:w-[630px] sm:max-w-[630px]";
const ADD_BUTTON_CLASS =
  "inline-flex h-[48px] w-[161px] shrink-0 items-center justify-center rounded-[12px] bg-[#5C88C5] px-[12px] py-[13px] text-[18px] font-semibold text-white transition-colors hover:bg-[#4A73A8] disabled:pointer-events-none disabled:opacity-50";

const TIER_OPTIONS = [
  { value: "1", label: "Level 1" },
  { value: "2", label: "Level 2" },
  { value: "3", label: "Level 3" },
];

export default function LevelManagementSection() {
  const [tier, setTier] = useState<LevelTier>(1);
  const [parentLevel1Id, setParentLevel1Id] = useState("");
  const [newValue, setNewValue] = useState("");
  const [rows, setRows] = useState<LevelTableRow[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [sortConfig, setSortConfig] = useState<TableSortConfig | undefined>();
  const [adding, setAdding] = useState(false);

  const [editState, setEditState] = useState<{
    open: boolean;
    id: string;
    label: string;
    originalLabel: string;
  }>({ open: false, id: "", label: "", originalLabel: "" });
  const [deleteState, setDeleteState] = useState<{
    open: boolean;
    id: string;
    label: string;
  }>({ open: false, id: "", label: "" });

  const [level1Options, setLevel1Options] = useState<
    { value: string; label: string }[]
  >([]);

  useEffect(() => {
    void levelApi.getCatalogLevels().then((levels) => {
      setLevel1Options(levels.map((l) => ({ value: l.id, label: l.label })));
    });
  }, [rows, tier]);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const result = await levelApi.getLevelRows(tier, {
        page: currentPage,
        pageSize: LEVELS_PER_PAGE,
        parentLevel1Id:
          tier === 2 && ADMIN_LEVEL_USE_MOCK ? parentLevel1Id : undefined,
        sortBy: sortConfig?.key,
        sortOrder: sortConfig?.direction,
      });
      setRows(result.data);
      setTotalItems(result.totalItems);
      setTotalPages(result.totalPages);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [tier, parentLevel1Id, currentPage, sortConfig]);

  const handleSort = (config: TableSortConfig | null) => {
    setSortConfig(config ?? undefined);
    if (currentPage !== 1) setCurrentPage(1);
  };

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  useEffect(() => {
    if (totalPages > 0 && currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  useEffect(() => {
    if (!ADMIN_LEVEL_USE_MOCK || tier !== 2) {
      setParentLevel1Id("");
      return;
    }
    if (!parentLevel1Id && level1Options.length > 0) {
      setParentLevel1Id(level1Options[0].value);
    }
  }, [tier, parentLevel1Id, level1Options]);

  const handleAdd = async () => {
    const validationError = getLabelTextValidationError(newValue);
    if (validationError) {
      showApiErrorToast({ message: validationError });
      return;
    }
    const trimmed = newValue.trim();
    if (tier === 2 && ADMIN_LEVEL_USE_MOCK && !parentLevel1Id) {
      showApiErrorToast({
        message: "Add at least one Level 1 value first",
      });
      return;
    }
    setAdding(true);
    try {
      const result = await levelApi.addLevelValue(tier, trimmed, {
        parentLevel1Id:
          tier === 2 && ADMIN_LEVEL_USE_MOCK ? parentLevel1Id : undefined,
      });
      setNewValue("");
      showApiSuccessToast(result.message, "Value added successfully");
      await fetchRows();
    } catch (e) {
      console.error(e);
      showApiErrorToast(e, "Could not add value. Please try again.");
    } finally {
      setAdding(false);
    }
  };

  const handleSaveEdit = async () => {
    const validationError = getLabelTextValidationError(editState.label, {
      previousLabel: editState.originalLabel,
    });
    if (validationError) {
      showApiErrorToast({ message: validationError });
      return;
    }
    try {
      const result = await levelApi.updateLevelValue(tier, editState.id, editState.label, {
        previousLabel: editState.originalLabel,
      });
      showApiSuccessToast(result.message, "Value updated successfully");
      setEditState({ open: false, id: "", label: "", originalLabel: "" });
      await fetchRows();
    } catch (e) {
      console.error(e);
      showApiErrorToast(e, "Could not update value. Please try again.");
    }
  };

  const handleConfirmDelete = async () => {
    try {
      const result = await levelApi.deleteLevelValue(tier, deleteState.id);
      showApiSuccessToast(result.message, "Value deleted successfully");
      setDeleteState({ open: false, id: "", label: "" });
      await fetchRows();
    } catch (e) {
      console.error(e);
      showApiErrorToast(e, "Could not delete value. Please try again.");
    }
  };

  const columns: TableColumn<LevelTableRow>[] = [
    {
      key: "sno",
      title: "S.No",
      minWidth: 68,
      maxWidth: 68,
      align: "center",
      render: (_, __, index) => (
        <div className="text-center text-[16px] font-medium text-[#1F1F1F]">
          {String((currentPage - 1) * LEVELS_PER_PAGE + index + 1).padStart(2, "0")}
        </div>
      ),
    },
    {
      key: "values",
      title: "Values",
      minWidth: 1024,
      sortable: true,
      sortKey: "label",
      render: (_, record) => (
        <div
          className="text-[16px] font-medium text-[#1F1F1F]"
          style={{ overflowWrap: "anywhere", whiteSpace: "pre-wrap" }}
        >
          {record.label}
        </div>
      ),
    },
    {
      key: "action",
      title: "Action",
      minWidth: 200,
      maxWidth: 200,
      align: "left",
      render: (_, record) => (
        <div className="flex items-center gap-[24px]">
          <button
            type="button"
            className="p-0"
            aria-label="Edit"
            onClick={() =>
              setEditState({
                open: true,
                id: record.id,
                label: record.label,
                originalLabel: record.label,
              })
            }
          >
            <SquarePen className="size-6 text-[#1C1B1F]" />
          </button>
          <button
            type="button"
            className="p-0"
            aria-label="Delete"
            onClick={() =>
              setDeleteState({
                open: true,
                id: record.id,
                label: record.label,
              })
            }
          >
            <Trash2 className="size-6 text-[#1C1B1F]" />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="bg-white rounded-[12px] px-4 sm:px-6 md:px-[32px] w-full overflow-hidden">
      <h2 className="pt-8 text-[20px] font-bold leading-[1.5] text-[#1F1F1F]">
        Set Level
      </h2>

      <div className="border-b border-[#EDEDED] pb-6 pt-8">
        <div className="flex w-full max-w-[412px] flex-col gap-[6px]">
          <div className={FORM_LABEL_CLASS}>Level</div>
          <FilterWithRadio
            options={TIER_OPTIONS}
            value={String(tier)}
            onChange={(v) => {
              setTier(Number(v) as LevelTier);
              setNewValue("");
              setCurrentPage(1);
            }}
            placeholder="Select level"
            showAbove={false}
            fieldStyle
          />
        </div>
      </div>

      <div className="py-6">
        <h3 className="mb-[15px] text-[20px] font-bold leading-[1.5] text-[#1F1F1F]">
          List of level {tier} values
        </h3>

        {ADMIN_LEVEL_USE_MOCK && tier === 2 ? (
          <div className="mb-4 flex w-full max-w-[412px] flex-col gap-[6px]">
            <div className={FORM_LABEL_CLASS}>Parent Level 1</div>
            <FilterWithRadio
              options={level1Options}
              value={parentLevel1Id}
              onChange={(value) => {
                setParentLevel1Id(value);
                setCurrentPage(1);
              }}
              placeholder="Select Level 1"
              showAbove={false}
              fieldStyle
            />
          </div>
        ) : null}

        <div className="mb-[19px] flex flex-col gap-[24px] sm:flex-row sm:items-center">
          <input
            placeholder="Enter value name"
            className={LEVEL_VALUE_INPUT_CLASS}
            {...bindLabelTextInputHandlers(newValue, setNewValue)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleAdd();
            }}
          />
          <button
            type="button"
            className={ADD_BUTTON_CLASS}
            disabled={adding}
            onClick={handleAdd}
          >
            {adding ? "Adding…" : "Add"}
          </button>
        </div>

        <div className="border border-[#EDEDED] rounded-md overflow-x-auto">
          <Table
            columns={columns}
            data={rows}
            rowKey="id"
            loading={loading}
            sticky
            scroll={{ x: 1292 }}
            sortConfig={sortConfig}
            onSort={handleSort}
          />
          {!loading && rows.length === 0 ? (
            <p className="text-center text-[14px] text-[#7B7B7B] py-8">
              No values yet. Add one above.
            </p>
          ) : null}
        </div>

        <ListPagination
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          totalItems={totalItems}
          itemsPerPage={LEVELS_PER_PAGE}
          itemsLabel="Values"
        />
      </div>

      <LevelValueEditModal
        isOpen={editState.open}
        value={editState.label}
        onChange={(label) => setEditState((s) => ({ ...s, label }))}
        onClose={() =>
          setEditState({ open: false, id: "", label: "", originalLabel: "" })
        }
        onSave={handleSaveEdit}
      />

      <Modal
        isOpen={deleteState.open}
        onClose={() => setDeleteState({ open: false, id: "", label: "" })}
        title="Delete value"
        width="520px"
        contentClassName="p-0"
      >
        <div className="p-6">
          <p className="text-[#7B7B7B] text-[18px]">
            Delete{" "}
            <span className="font-semibold text-[#1F1F1F]">
              {deleteState.label}
            </span>
            ?
          </p>
        </div>
        <div className="flex justify-end border-t border-gray-200 p-4 gap-4">
          <Button
            type="button"
            variant="light"
            size="md"
            className="w-[120px] h-[48px] border border-[#5C88C5]"
            onClick={() => setDeleteState({ open: false, id: "", label: "" })}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="primary"
            size="md"
            className="w-[120px] h-[48px] bg-[#5C88C5] text-white"
            onClick={handleConfirmDelete}
          >
            Delete
          </Button>
        </div>
      </Modal>
    </div>
  );
}
